import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { runScreener, ScreenerResult } from '../services/ScreenerService';
import { IconFilter, IconSearch, IconZap, IconTable, IconStrategy, IconBrain } from '../components/Icons';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config';

interface MomentumResult {
    ticker: string;
    week_ago_alpha: number;
    today_alpha: number;
    change_raw: number;
    change_pct: number;
}

// --- Helper types for sorting ---
type SortDirection = 'asc' | 'desc';
interface SortConfig {
    key: string | null;
    direction: SortDirection;
}

type Mode = 'NLP' | 'MOMENTUM';

const QUICK_SCANS = [
    { label: 'High Alpha Tech', query: 'Find Tech stocks with high alpha score against QQQ' },
    { label: 'Defensive Quality', query: 'Find stocks with high bear alpha and low volatility' },
    { label: 'Pairs Candidates', query: 'Find cointegrated pairs in the SP100' },
];

const MarketScreenerView: React.FC = () => {
    const { currentUser } = useAuth();
    
    // --- MODE STATE ---
    const [mode, setMode] = useState<Mode>('NLP');

    // --- NLP STATE ---
    const [query, setQuery] = useState('');
    const [nlpResults, setNlpResults] = useState<ScreenerResult[]>([]);
    const [summary, setSummary] = useState('');

    // --- MOMENTUM STRATEGY STATE ---
    const [momParams, setMomParams] = useState({
        universe: 'SP100',
        lookbackDays: 30,      // Window for Alpha Calc
        momentumWindow: 5,     // Window for Velocity Calc
        threshold: 0.5,        // Significance
        benchmark: 'QQQ',
        alphaMetric: 'alpha_score' // <--- NEW: Default to Total Alpha
    });
    const [momResults, setMomResults] = useState<MomentumResult[]>([]);

    // --- SHARED STATE ---
    const [isRunning, setIsRunning] = useState(false);
    const [credits, setCredits] = useState<number | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'desc' });

    // --------------------------------------------------------------------------------
    // HANDLERS
    // --------------------------------------------------------------------------------

    // 1. Run NLP Scan
    const handleRunNlpScan = async (scanQuery: string = query) => {
        if (!scanQuery.trim() || !currentUser) return;
        setIsRunning(true);
        setNlpResults([]);
        setSummary('');
        setQuery(scanQuery);
        setSortConfig({ key: null, direction: 'desc' });

        try {
            const token = await currentUser.getIdToken();
            const data = await runScreener(scanQuery, token);
            if (data.results) setNlpResults(data.results);
            if (data.summary) setSummary(data.summary);
            if (data.new_credit_balance !== undefined) setCredits(data.new_credit_balance);
            toast.success("NLP Scan Complete");
        } catch (err: any) {
            toast.error(err.message || "Scan failed");
        } finally {
            setIsRunning(false);
        }
    };

    // 2. Run Momentum Strategy
    const handleRunMomentum = async () => {
        if (!currentUser) return;
        setIsRunning(true);
        setMomResults([]);
        setSortConfig({ key: 'change_pct', direction: 'desc' });

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/trading_signals/alpha_decay`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    params: momParams // This now includes the alphaMetric choice
                })
            });

            if (!response.ok) throw new Error("Strategy calculation failed");
            
            const data = await response.json();
            if (data.momentum_data) {
                setMomResults(data.momentum_data);
                toast.success(`Calculated ${momParams.alphaMetric} Velocity`);
            }
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setIsRunning(false);
        }
    };

    // 3. Sorting Logic (Shared)
    const activeResults = mode === 'NLP' ? nlpResults : momResults;

    const sortedResults = useMemo(() => {
        if (!sortConfig.key) return activeResults;

        return [...activeResults].sort((a: any, b: any) => {
            const aValue = a[sortConfig.key!];
            const bValue = b[sortConfig.key!];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            const strA = String(aValue).toLowerCase();
            const strB = String(bValue).toLowerCase();
            if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [activeResults, sortConfig]);

    const handleSort = (key: string) => {
        let direction: SortDirection = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') direction = 'asc';
        setSortConfig({ key, direction });
    };

    const renderSortIcon = (columnKey: string) => {
        if (sortConfig.key !== columnKey) return <span className="text-slate-300 ml-1 opacity-0 group-hover:opacity-50">↕</span>;
        return <span className="text-purple-600 ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
    };

    const getScoreColor = (val: number | undefined) => {
        if (val === undefined) return 'text-slate-500';
        if (val > 0.5) return 'text-emerald-600 font-bold';
        if (val < -0.5) return 'text-red-600 font-bold';
        return 'text-slate-700';
    };

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden">
            {/* LEFT: CONTROL PANEL */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <IconFilter className="text-purple-600" /> Market Scanner
                    </h2>
                    <p className="text-slate-500 text-sm">Professional Universe Scanning.</p>
                </div>

                {/* MODE SWITCHER */}
                <div className="flex p-1 bg-slate-100 rounded-lg mb-6">
                    <button 
                        onClick={() => setMode('NLP')}
                        className={`flex-1 py-2 text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-all ${mode === 'NLP' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <IconBrain size={14} /> AI Query
                    </button>
                    <button 
                        onClick={() => setMode('MOMENTUM')}
                        className={`flex-1 py-2 text-xs font-bold rounded-md flex items-center justify-center gap-2 transition-all ${mode === 'MOMENTUM' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <IconStrategy size={14} /> Alpha Velocity
                    </button>
                </div>

                {/* --- INPUTS FOR NLP MODE --- */}
                {mode === 'NLP' && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Natural Language Query</label>
                            <div className="relative">
                                <textarea 
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleRunNlpScan(); } }}
                                    className="w-full border border-slate-300 rounded-lg p-3 text-sm h-24 resize-none focus:ring-2 focus:ring-purple-500 outline-none shadow-inner"
                                    placeholder="e.g., Find stocks with high Sharpe Ratio and low Beta vs SPY..."
                                />
                                <button 
                                    onClick={() => handleRunNlpScan(query)}
                                    disabled={isRunning || !query}
                                    className="absolute bottom-3 right-3 bg-purple-600 text-white p-2 rounded-md hover:bg-purple-700 disabled:opacity-50 transition-all shadow-sm"
                                >
                                    {isRunning ? <span className="animate-spin block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/> : <IconSearch size={16} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Quick Scans</label>
                            <div className="space-y-2">
                                {QUICK_SCANS.map((scan, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleRunNlpScan(scan.query)}
                                        disabled={isRunning}
                                        className="w-full text-left p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:shadow-md hover:border-purple-200 transition-all group"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-slate-700 group-hover:text-purple-700">{scan.label}</span>
                                            <IconZap size={14} className="text-slate-300 group-hover:text-purple-400" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {summary && (
                            <div className="mt-6 pt-6 border-t border-slate-100">
                                <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">AI Execution Plan</h3>
                                <div className="bg-slate-900 text-slate-300 p-4 rounded-lg text-xs leading-relaxed shadow-md border border-slate-700 font-mono">
                                    {summary}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* --- INPUTS FOR MOMENTUM MODE (PRO CONTROLS) --- */}
                {mode === 'MOMENTUM' && (
                    <div className="animate-fade-in space-y-5">
                        <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-800 leading-relaxed">
                            <strong>Strategy:</strong> Identifies stocks where the <u>{momParams.alphaMetric.replace('_', ' ')}</u> is accelerating or decelerating compared to {momParams.momentumWindow} days ago.
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Stock Universe</label>
                            <select 
                                className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                                value={momParams.universe}
                                onChange={(e) => setMomParams({...momParams, universe: e.target.value})}
                            >
                                <option value="SP100">S&P 100</option>
                                <option value="NASDAQ100">NASDAQ 100</option>
                                <option value="WATCHLIST">My Watchlist</option>
                            </select>
                        </div>

                        {/* --- NEW: ALPHA METRIC SELECTOR --- */}
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Alpha Metric</label>
                            <select 
                                className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                                value={momParams.alphaMetric}
                                onChange={(e) => setMomParams({...momParams, alphaMetric: e.target.value})}
                            >
                                <option value="alpha_score">Total Alpha (Bull + Bear)</option>
                                <option value="bull_alpha">Bull Alpha (Upside Strength)</option>
                                <option value="bear_alpha">Bear Alpha (Downside Defense)</option>
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Lookback (Days)</label>
                                <input 
                                    type="number" 
                                    className="w-full p-2 border border-slate-300 rounded text-sm"
                                    value={momParams.lookbackDays}
                                    onChange={(e) => setMomParams({...momParams, lookbackDays: Number(e.target.value)})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Velocity Window</label>
                                <input 
                                    type="number" 
                                    className="w-full p-2 border border-slate-300 rounded text-sm bg-purple-50 border-purple-200"
                                    value={momParams.momentumWindow}
                                    onChange={(e) => setMomParams({...momParams, momentumWindow: Number(e.target.value)})}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Threshold (%)</label>
                                <input 
                                    type="number" step="0.1"
                                    className="w-full p-2 border border-slate-300 rounded text-sm"
                                    value={momParams.threshold}
                                    onChange={(e) => setMomParams({...momParams, threshold: Number(e.target.value)})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Benchmark</label>
                                <select 
                                    className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                                    value={momParams.benchmark}
                                    onChange={(e) => setMomParams({...momParams, benchmark: e.target.value})}
                                >
                                    <option value="QQQ">QQQ</option>
                                    <option value="SPY">SPY</option>
                                    <option value="IWM">IWM</option>
                                </select>
                            </div>
                        </div>

                        <button 
                            onClick={handleRunMomentum}
                            disabled={isRunning}
                            className="w-full mt-4 bg-purple-600 text-white font-bold py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-all shadow-md flex items-center justify-center gap-2"
                        >
                            {isRunning ? <span className="animate-spin block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/> : <IconSearch size={16} />}
                            Calculate Alpha Velocity
                        </button>
                    </div>
                )}
            </div>

            {/* RIGHT: RESULTS GRID */}
            <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
                {activeResults.length > 0 ? (
                    <div className="flex-1 flex flex-col p-6 overflow-hidden">
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden">
                            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                <h3 className="font-bold text-slate-700 flex items-center gap-2">
                                    <IconTable size={18} /> {mode === 'NLP' ? 'Scan Results' : 'Momentum Signals'} ({activeResults.length})
                                </h3>
                                <button className="text-xs font-bold text-purple-600 bg-purple-50 px-3 py-1.5 rounded hover:bg-purple-100 transition-colors">
                                    Export CSV
                                </button>
                            </div>
                            
                            <div className="flex-1 overflow-auto custom-scrollbar">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            {/* --- HEADERS --- */}
                                            <th onClick={() => handleSort('ticker')} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors group select-none">
                                                Ticker {renderSortIcon('ticker')}
                                            </th>
                                            
                                            {mode === 'NLP' ? (
                                                 Object.keys(activeResults[0])
                                                    .filter(k => k !== 'ticker' && k !== 'category')
                                                    .map(k => (
                                                        <th key={k} onClick={() => handleSort(k)} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 whitespace-nowrap cursor-pointer hover:bg-slate-100">
                                                            {k.replace(/_/g, ' ')} {renderSortIcon(k)}
                                                        </th>
                                                    ))
                                            ) : (
                                                <>
                                                    <th onClick={() => handleSort('week_ago_alpha')} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 cursor-pointer hover:bg-slate-100">
                                                        Alpha (Prev) {renderSortIcon('week_ago_alpha')}
                                                    </th>
                                                    <th onClick={() => handleSort('today_alpha')} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 cursor-pointer hover:bg-slate-100">
                                                        Alpha (Today) {renderSortIcon('today_alpha')}
                                                    </th>
                                                    <th onClick={() => handleSort('change_pct')} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 cursor-pointer hover:bg-slate-100">
                                                        Velocity % {renderSortIcon('change_pct')}
                                                    </th>
                                                    <th onClick={() => handleSort('change_raw')} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 cursor-pointer hover:bg-slate-100">
                                                        Raw Change {renderSortIcon('change_raw')}
                                                    </th>
                                                </>
                                            )}
                                            
                                            {(activeResults[0] as any).category && (
                                                <th onClick={() => handleSort('category')} className="px-6 py-3 font-bold text-xs uppercase border-b border-slate-200 cursor-pointer hover:bg-slate-100">
                                                    Category {renderSortIcon('category')}
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {sortedResults.map((row: any, i) => (
                                            <tr key={i} className="hover:bg-purple-50/30 transition-colors">
                                                <td className="px-6 py-3 font-bold text-slate-800 border-r border-slate-100 bg-slate-50/30">
                                                    {row.ticker}
                                                </td>

                                                {mode === 'NLP' ? (
                                                    Object.entries(row)
                                                        .filter(([k]) => k !== 'ticker' && k !== 'category')
                                                        .map(([k, v], j) => (
                                                            <td key={j} className="px-6 py-3 font-mono text-slate-600">
                                                                <span className={typeof v === 'number' ? getScoreColor(v) : ''}>
                                                                    {typeof v === 'number' ? v.toFixed(2) : String(v)}
                                                                </span>
                                                            </td>
                                                        ))
                                                ) : (
                                                    <>
                                                        <td className="px-6 py-3 font-mono text-slate-600">{row.week_ago_alpha?.toFixed(2)}</td>
                                                        <td className="px-6 py-3 font-mono text-slate-600">{row.today_alpha?.toFixed(2)}</td>
                                                        <td className="px-6 py-3 font-mono font-bold">
                                                            <span className={row.change_pct > 0 ? 'text-emerald-600' : 'text-red-600'}>
                                                                {row.change_pct > 0 ? '+' : ''}{row.change_pct?.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-3 font-mono text-slate-500">{row.change_raw?.toFixed(2)}</td>
                                                    </>
                                                )}

                                                {row.category && (
                                                    <td className="px-6 py-3">
                                                        <span className="inline-block bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-1 rounded border border-slate-200 uppercase">
                                                            {row.category}
                                                        </span>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10">
                        {isRunning ? (
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
                                <p className="animate-pulse font-medium text-purple-600">
                                    {mode === 'NLP' ? 'AI is generating Python code plan...' : 'Calculating historical Alpha Velocity...'}
                                </p>
                            </div>
                        ) : (
                            <>
                                <IconFilter size={64} className="mb-4 opacity-10" />
                                <h3 className="text-lg font-medium text-slate-500">Ready to Scan</h3>
                                <p className="text-sm max-w-sm text-center mt-2 opacity-70">
                                    {mode === 'NLP' 
                                        ? "Select a Quick Scan or describe your strategy in plain English." 
                                        : "Configure parameters to detect Alpha Momentum regime changes."}
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketScreenerView;